import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { withStyles, createStyles } from '@mui/styles'
import { Tooltip, Typography, IconButton, Link, Badge, Paper, Chip, Input } from '@mui/material'
import {
    Add, CheckBox, CheckBoxOutlineBlank, CreateOutlined, DeleteOutlined, EditOutlined, FileCopyOutlined, FilterListOutlined,
    GavelOutlined, GetAppOutlined, Link as LinkIcon, MoreVert, PublishOutlined, History
} from '@mui/icons-material'
import { muiOptions, MuiProps, defaultStyles, defaultColors } from '../../infrastructure/materialUiThemeProvider'
import { t } from '../../infrastructure/i18nextHelper'
import * as api from '../../infrastructure/api'
import { LoaderStatusContainer } from '../../infrastructure/loader'
import { formatFilename } from '../../infrastructure/excelExport'
import guid, { Guid } from '../../infrastructure/guid'
import { Claims } from '../../infrastructure/signIn/models'
import { hasClaim, hasDealClaim } from '../../infrastructure/signIn/userContext'
import { hasFeature, FeatureContainer } from '../../infrastructure/feature'
import { historyDialog } from '../common/history/dialog'
import {
    ColumnDescriptor, CustomDialog, DataTable, Switch, Menu, NumberField, SearchField, SapChip, LabelButton
} from '../common/customComponents'
import { VesselEditDialogContainer } from '../vessels/vesselEdit/vesselEditStore'
import { StockMovementContainer, movementDialog } from './movementEdit/stockMovementStore'
import { StockBoardContainer } from './stockBoardStore'
import {
    MovementListItem, MovementStatus, MovementTypeNameFromMovementType, MovementTypeFromMovementTypeName,
    DuplicateMovementCommand, MovementForm, DealLabelId, MovementType, MeanOfTransportationType,
    MovementStatusNameFromMovementStatus, MovementStatusFromMovementStatusName, SapFlow, SapFlowStepStatus,
} from './stockModels'
import { useActionDebounceWithArgs } from '../common/debounce'
import { MovementTypeIcon, MovementMotIcon, meanOfTransportationPopupType, StatusIcon } from './_movementIcon'
import { CustomButton } from '../common/components/button'
import { handleSapCall } from './movementEdit/movementForms/sap/sapHandlers'
import { isDealRequired, isDisplayable, isEnabled } from './movementEdit/movementForms/sap/createInSap/formRequirements'
import { snackbars } from '../../infrastructure/snackbars'
import { MovementStatusIcon } from './MovementStatusIcon'
import { MovementMessageContainer } from './movementMessageStore'
import { popupNavigator } from '../../infrastructure/popupNavigator'
import { ActionDescriptor, GroupDescriptor } from '../common/components/table/table'
import { ConfirmDealAssignation, SelectAssignableDeals } from '../common/assign/assignDealPopup'
import { Deal } from '../deals/dealModels'
import { CommentButton } from '../common/comment/commentButton'
import { copyHtmlElement } from '../../infrastructure/copy'
import { isMultiSapFlow, PurchaseRelease, SapTypes, steps, isTransferSapType } from '../common/sapHelper'
import { SapFilters } from './filters/_sapFilters'
import { StockFiltersContainer } from './filters/filtersStore'

const EnterKeyCode = 13

type MovementTableProps = {
    className?: string
    movementPopupHalfBottom?: boolean
}

function _MovementTable({ classes, className, movementPopupHalfBottom }: MuiProps & MovementTableProps) {
    let store = StockBoardContainer.useContainer()
    let stockFiltersStore = StockFiltersContainer.useContainer()
    let stockEdit = StockMovementContainer.useContainer()
    let vesselEdit = VesselEditDialogContainer.useContainer()
    let loader = LoaderStatusContainer.useContainer()
    let features = FeatureContainer.useContainer()
    let movementMessageStore = MovementMessageContainer.useContainer()

    let [warningFilterAnchor, setWarningFilterAnchor] = useState<Element | null>(null)
    let [nominationFilterAnchor, setNominationFilterAnchor] = useState<Element | null>(null)
    let [dealFilterAnchor, setDealFilterAnchor] = useState<Element | null>(null)
    let [movementStatusFilterAnchor, setMovementStatusFilterAnchor] = useState<Element | null>(null)
    let [motFilterAnchor, setMotFilterAnchor] = useState<Element | null>(null)
    let [movementTypeFilterAnchor, setMovementTypeFilterAnchor] = useState<Element | null>(null)
    let [selectedMovements, setSelectedMovements] = useState<MovementListItem[]>([])
    let [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false)
    let [currentMovement, setCurrentMovement] = useState<MovementListItem | null>(null)
    let [openAssignDealPopup, setOpenAssignDealPopup] = useState<boolean>(false)
    let [assignableDealsFromMovement, setAssignableDealsFromMovement] = useState<Deal[] | null>(null)
    let [dealIdToAssign, setDealIdToAssign] = useState<Guid | null>(null)
    let [isConfirmDealOpen, setIsConfirmDealOpen] = useState<boolean>(false)
    let [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState<boolean>(false)

    let changeMovementQuantity = (function () {
        let [isOpen, setIsOpen] = useState<boolean>(false)
        let [movementId, setMovementId] = useState<string | null>(null)
        let [quantity, setQuantity] = useState<number | null>(0)

        let ref = useRef<{ focus: () => void, select: () => void } | null>(null)

        let open = (movement: MovementListItem) => {
            setQuantity(movement.volume)
            setMovementId(movement.id)
            setIsOpen(true)
            setImmediate(() => { ref?.current?.focus(); ref?.current?.select() })
        }

        let save = async () => {
            if (!quantity || !movementId) return
            setIsOpen(false)
            await api.post('stock/movement/quantity', { movementId, quantity })
            await store.load()
        }

        let close = () => setIsOpen(false)

        return { ref, isOpen, quantity, open, close, change: setQuantity, save }
    })()

    let batchNominateVolume = (function () {
        let [isOpen, setIsOpen] = useState<boolean>(false)
        let [movementIds, setMovementIds] = useState<string[] | null>(null)
        let [nominatedVolume, setNominatedVolume] = useState<number | null>(0)

        let ref = useRef<{ focus: () => void, select: () => void } | null>(null)

        let open = (movementIds: Guid[]) => {
            setIsOpen(true)
            setMovementIds(movementIds)
            setImmediate(() => { ref?.current?.focus(); ref?.current?.select() })
        }

        let save = async () => {
            if (!nominatedVolume || !movementIds) return
            close()
            let movementsNumber = movementIds.length
            if (movementsNumber > 0) {
                await api.post('stock/movement/batchNominateVolume', { nominatedVolume, movementIds })
                snackbars.success(t('httpSuccess.nominationMovementsUpdated', { movementsNumber }))
                setSelectedMovements([])
                await store.load()
            }
        }

        let close = () => setIsOpen(false)

        return { ref, isOpen, nominatedVolume, open, close, change: setNominatedVolume, save }
    })()

    useEffect(() => { if (!stockEdit.isOpen && !vesselEdit.isOpen) store.load() }, [stockEdit.isOpen, vesselEdit.isOpen])

    useEffect(() => {
        store.loadCounterpartys()
    }, [])

    let onSelect = (movement: MovementListItem) => {
        movement.movementType != MovementType.MktSale && movementDialog.open(movement.id, movementPopupHalfBottom)
    }

    let openMovementCreationPopup = () => movementDialog.open(null, movementPopupHalfBottom)

    let toggleSwitch = () => {
        store.changeSwitchState(store.switchFilter == store.outValue ? store.inValue : store.outValue)
    }

    let deleteMovements = async () => {
        setIsConfirmDeleteOpen(false)
        let notMktSalesMovements = selectedMovements.filter(x => x.movementType != MovementType.MktSale)
        if (notMktSalesMovements?.length === 0) return
        let request = { movementIds: notMktSalesMovements.map(x => x.id) }
        await api.post('stock/movement/batchDelete', request)
        store.load()
        movementMessageStore.setSelectedMovementIds([])
        setSelectedMovements([])
    }

    let duplicateMovement = async (selectedMovement: MovementListItem): Promise<void> => {
        let command: DuplicateMovementCommand = {
            sourceId: selectedMovement.id,
            duplicateId: guid.createNew()
        }
        await api.post('stock/movement/duplicate', command)
        movementDialog.open(command.duplicateId)
    }

    let handleDuplicateDebouncer = useActionDebounceWithArgs(duplicateMovement)

    let getTotalFilteredMovementsVolume = () => {
        let currentMovements = store.stockDateFilter
            ? store.stockDateFilterMovements
            : store.movements
        return currentMovements.reduce((acc, cur) => acc + (cur.volume ?? 0), 0)
    }

    let getTotalFilteredMovementsNominatedVol = () => {
        let currentMovements = store.stockDateFilter
            ? store.stockDateFilterMovements
            : store.movements
        return currentMovements.reduce((acc, cur) => acc + (cur.nominatedVolume ?? 0), 0)
    }

    let getSapFlow = (sapType: SapTypes, movement: MovementForm): SapFlow => {

        if (movement.movementType == MovementType.Purchase || movement.movementType == MovementType.Sale)
            if (isTransferSapType(sapType))
                return movement.secondSapFlow!

        return movement.mainSapFlow
    }

    let handleClickOnSapButton = async (sapType: SapTypes, movementId: Guid, features): Promise<void> => {
        let movementForm = await api.get<MovementForm>(`stock/movement/${movementId}`)
        let hasDeal = !!(await api.get<DealLabelId | null>(`deal/idLabel/fromMovement/${movementId}`))
        if (isDealRequired(sapType) && !hasDeal) {
            snackbars.error(t('stock.label.movement.sapAction.noAssociatedDeal'))
            return
        }

        if (!isEnabled(sapType, movementForm, getSapFlow(sapType, movementForm), hasDeal)) {
            snackbars.error(t('stock.label.movement.sapAction.invalidMovementForm'))
            return
        }

        try {
            await handleSapCall(sapType, movementForm, async () => {
                await store.load()
            })
        }
        catch (err) {
            throw err
        }
        finally {
            if (sapType !== PurchaseRelease.sapType)
                await store.load()
        }
    }

    let getMeanOfTransportationId = (movement: MovementListItem): string | null => {
        switch (movement.meanOfTransportation) {
            case 'Barge':
            case 'Ship':
                return movement.associatedVesselId
            case 'Road':
                return movement.associatedTruckId
            default:
                return null
        }
    }

    let isMovementTableFullWidth = store.isMovementTableFullWidth()

    let showNomination = isMovementTableFullWidth
        && (hasFeature('DailyNomination') || hasFeature('WeeklyNomination'))

    let showTobs = isMovementTableFullWidth
        && hasFeature('UseObservedTemperatureByDefault')

    let showExternalNumber = isMovementTableFullWidth
        && hasFeature('MovementExternalNumber')

    let showCounterparty = isMovementTableFullWidth
        && hasFeature('NominationByCounterparty')

    let sapColumnGroup = 'sapDetails'

    let commentColumn: ColumnDescriptor<MovementListItem>[] =
        isMovementTableFullWidth
            ? [{
                preventRowClick: true,
                textAlign: 'center',
                name: t('stock.label.movement.comments'),
                value: () => '',
                htmlFor: x => <CommentButton
                    associationKey={`movement-${x.id}`}
                    hideIfNoContent />
            }] : []

    let getMovementTableColumns = () => {
        let initialMovementColumns: ColumnDescriptor<MovementListItem>[] = [
            { name: t('stock.label.movement.date'), value: x => x.date ? moment(x.date).format('MM/DD') : '' },
            {
                name: t('stock.label.movement.type'), htmlFor: x => <MovementTypeIcon movementType={x.movementType ?? MovementType.Unknown} />,
                onHeaderClick: isMovementTableFullWidth ? undefined : (event: React.MouseEvent) => { setMovementTypeFilterAnchor(event?.currentTarget) },
                hasHeaderHighlight: isMovementTableFullWidth ? undefined : () => !!store.selectedMovementType
            },
            {
                name: t('stock.label.movement.referenceExt'), value: x => x.reference, wrapText: true,
                htmlFor: x => <Tooltip title={<Typography variant='body1'>{x.reference ?? ''}</Typography>}>
                    <div className={classes.overflow}>{x.reference}</div>
                </Tooltip>
            },
            {
                name: t('stock.label.originDestination'), htmlFor: x =>
                    <div className={classes.textSubTextContainer}>
                        <div>{x.text}</div>
                        <div className={classes.subText}>{x.subText}</div>
                    </div>
            },
            {
                name: t('stock.movementTable.ediIn'), value: x => x.destinationExternalNumber,
                hidden: !showExternalNumber
            },
            {
                name: t('stock.movementTable.ediOut'), value: x => x.originExternalNumber,
                hidden: !showExternalNumber
            },
            {
                name: t('stock.movementTable.tobs'), value: x => x.observedTemperature,
                hidden: !showTobs,
                textAlign: 'right'
            },
            {
                name: t('stock.movementTable.volumeTobs'), value: x => x.observedQuantity,
                hidden: !showTobs,
                textAlign: 'right'
            },
            {
                name: t('stock.label.movement.counterparty'),
                value: x => x.counterparty,
                hidden: !showCounterparty
            },
            {
                name: t('stock.label.movement.mot'), htmlFor: x => {
                    let meanOfTransportationId = getMeanOfTransportationId(x)
                    let tooltip: string | null = null
                    if (x.meanOfTransportation === 'Ship' || x.meanOfTransportation === 'Barge')
                        tooltip = x.vesselReference
                    else if (x.meanOfTransportation === 'Road')
                        tooltip = x.truckReference

                    return (<IconButton
                        style={{ color: !!meanOfTransportationId ? defaultColors.lightBlue.main.color : '' }}
                        onClick={(event) => {
                            if (meanOfTransportationId) {
                                event.stopPropagation()
                                popupNavigator.open(meanOfTransportationPopupType(x.meanOfTransportation!), meanOfTransportationId)
                            }
                        }}>
                        <MovementMotIcon meanOfTransportation={x.meanOfTransportation ?? ''} tooltip={tooltip} />
                    </IconButton>)
                },
                onHeaderClick: isMovementTableFullWidth ? undefined : (event: React.MouseEvent) => { setMotFilterAnchor(event?.currentTarget) },
                hasHeaderHighlight: isMovementTableFullWidth ? undefined : () => !!store.selectedMot && store.selectedMot?.length > 0
            },
            {
                name: t('stock.label.movement.nomination'),
                hidden: !showNomination,
                htmlFor: x => (x.nomination
                    ? <CheckBox />
                    : <CheckBoxOutlineBlank />
                )
            },
            {
                name: t('stock.movementTable.nominatedVolume', { unit: store.unit }),
                value: x => x.nominatedVolume ? Math.round(x.nominatedVolume) : null,
                hidden: !showNomination,
                textAlign: 'right',
                total: getTotalFilteredMovementsNominatedVol()
            },
            {
                name: t('stock.label.movement.volume', { unit: store.unit }),
                textAlign: 'right',
                total: getTotalFilteredMovementsVolume(),
                value: x => x.volume ? Math.round(x.volume).toString() : null
            },
            {
                name: t('stock.label.movement.status'), value: x => MovementStatus[x.movementStatus ?? -1],
                htmlFor: x => <MovementStatusIcon movementItem={x} classes={classes} />,
                onHeaderClick: isMovementTableFullWidth ? undefined : (event: React.MouseEvent) => { setMovementStatusFilterAnchor(event?.currentTarget) },
                hasHeaderHighlight: isMovementTableFullWidth ? undefined : () => !!store.selectedStatus && store.selectedStatus?.length > 0
            },
            ...commentColumn,
            {
                name: t('stock.movementTable.dealNumber'),
                value: x => x.dealNumber,
                hidden: !isMovementTableFullWidth,
                htmlFor: x => (
                    <Link underline='hover' onClick={(e) => { e.stopPropagation(); popupNavigator.open('deal', x.associatedDealId) }}>
                        {x.dealNumber}
                    </Link>
                )
            }
        ]

        if (hasClaim(Claims.StockManager)) {
            let sapFlowStep = (x: MovementListItem, stepStatus: SapFlowStepStatus, index: number, bottonIndex: number) => {
                let stepNames = x.mainSapFlowListItem?.steps.map(stepStatus => stepStatus.name)
                let flowType = store.mapFlowType(stepNames, x.movementType)
                let mappedSapType = store.mapToSapType(stepStatus.name, x.movementType!, flowType)
                if ((!isMultiSapFlow(x) && bottonIndex == index || isMultiSapFlow(x) && !stepStatus.isCompleted)
                    && isMovementTableFullWidth
                    && mappedSapType !== null
                    && isDisplayable(mappedSapType!, features))
                    return (
                        <CustomButton
                            key={index}
                            className={classes.sapButton}
                            classes={{ label: classes.noPadding }}
                            label={stepStatus.name}
                            onClick={(event) => { event.stopPropagation(); handleClickOnSapButton(mappedSapType!, x.id, features) }}
                            debouncerDelay={5000}
                        />)
                else
                    return <SapChip
                        className='movementList'
                        key={index}
                        hasValue={stepStatus.isCompleted}
                        isFromSap={stepStatus.isFromSap}
                        label={isMovementTableFullWidth && stepStatus.isCompleted ? stepStatus.value : stepStatus.name} />
            }

            initialMovementColumns.push(
                {
                    name: t('stock.label.movement.sap'), value: x => MovementStatus[x.movementStatus ?? -1],
                    defaultGroup: sapColumnGroup,
                    htmlFor: x => {
                        if (x.nomination) return <></>
                        let buttonIndex = x.mainSapFlowListItem?.steps.findIndex(y => !y.isCompleted)
                        let sapFlowSteps = steps(x, isMovementTableFullWidth)
                        return (<>
                            <span style={{ padding: '0 0.5em 0 0' }}>
                                {sapFlowSteps.mainSapFlow.map((stepStatus, index) => sapFlowStep(x, stepStatus, index, buttonIndex))}
                            </span>
                            {!!sapFlowSteps.secondSapFlow && sapFlowSteps.secondSapFlow.length > 0
                                && <span>--
                                    <span style={{ padding: '0 0 0 0.5em' }}>
                                        {sapFlowSteps.secondSapFlow.map((stepStatus, index) => sapFlowStep(x, stepStatus, index, buttonIndex))}
                                    </span>
                                </span>}
                        </>)
                    }
                })
        }

        if (isMovementTableFullWidth) {
            let sapColumns = [
                {
                    name: t('stock.movementTable.sapVendor'),
                    value: (x: MovementListItem) => x.movementType === MovementType.Purchase ? x.dealSapVendor : '',
                    groupedBy: sapColumnGroup
                },
                {
                    name: t('stock.movementTable.sapShipTo'),
                    value: (x: MovementListItem) => x.movementType === MovementType.Sale ? x.dealSapShipTo : '',
                    groupedBy: sapColumnGroup
                },
                {
                    name: t('stock.movementTable.originPlant'),
                    value: (x: MovementListItem) => x.mainSapFlowListItem.originPlant,
                    groupedBy: sapColumnGroup
                },
                {
                    name: t('stock.movementTable.destinationPlant'),
                    value: (x: MovementListItem) => x.mainSapFlowListItem.destinationPlant,
                    groupedBy: sapColumnGroup
                },
                {
                    name: t('stock.movementTable.originStorageLocation'),
                    value: (x: MovementListItem) => x.mainSapFlowListItem.originStorageLocation,
                    groupedBy: sapColumnGroup
                },
                {
                    name: t('stock.movementTable.destinationStorageLocation'),
                    value: (x: MovementListItem) => x.mainSapFlowListItem.destinationStorageLocation,
                    groupedBy: sapColumnGroup
                },
                {
                    name: t('stock.movementTable.materialDocument'),
                    value: (x: MovementListItem) => x.material,
                    groupedBy: sapColumnGroup
                }
            ]

            initialMovementColumns = [...initialMovementColumns, ...sapColumns]
        }

        return initialMovementColumns
    }

    let movementTableGroups: GroupDescriptor[] = [
        { name: 'details', title: '', colSpan: 15 },
        { name: sapColumnGroup, title: t('deals.label.sapDetails'), withHiddenColumns: true },
    ]

    let filterOnMovementType = async (movementTypeName: string) => {
        store.deleteStockDateFilter()
        store.setSelectedMovementType(MovementTypeFromMovementTypeName(movementTypeName))
    }


    let onMotChange = async (mots) => {
        store.setSelectedMot(mots.map(mot => MeanOfTransportationType(mot)))
    }

    let onStatusChange = async (statuses) => {
        store.setSelectedStatus(statuses.map(status => MovementStatusFromMovementStatusName(status)))
    }

    let getMovementItems = () => {
        return (store.stockDateFilter
            ? store.stockDateFilterMovements
            : store.movements).slice(0, 200)
    }

    let getMenuItemYesNo = () =>
        [{ value: '', text: t('stock.movementTable.menuChoiceAll') },
        { value: 'true', text: t('stock.movementTable.menuChoiceYes') },
        { value: 'false', text: t('stock.movementTable.menuChoiceNo') }]

    let getMenuLabelYesNo = (menuChoice: string) => {
        if (menuChoice === 'true') return `:  ${t('stock.movementTable.menuChoiceYes')}`
        if (menuChoice === 'false') return `: ${t('stock.movementTable.menuChoiceNo')}`
        else return ''
    }

    let exportMovements = async () => {
        let filename = formatFilename('Movement.xlsx')

        let filters = stockFiltersStore.filters.values

        filters['in'] = store.switchFilter == store.inValue
        filters['out'] = store.switchFilter == store.outValue
        filters['movementType'] = store.selectedMovementType
        filters['meanOfTansportation'] = store.selectedMot
        filters['company'] = filters.companies
        filters['dutyStatus'] = filters.dutyStatuses

        await api.post(`stock/movement/export`, {
            filename: filename,
            movementIds: selectedMovements.map(x => x.id),
            filters: {
                ...filters,
                withMktSale: hasFeature('MktSalesInMovementTable')
            }
        })

        let url = `${api.url}download/${filename}`

        window.open(url, '_blank')
    }

    function reduceToActivatedFeatureToggles(value: string): boolean {
        if (value === MovementTypeNameFromMovementType(MovementType.StatusChange))
            return hasFeature('StatusChangeMovement')
        else if (value === MovementTypeNameFromMovementType(MovementType.Gains)
            || value === MovementTypeNameFromMovementType(MovementType.Losses))
            return hasFeature('MovementGainsAndLosses')
        return true
    }

    let OnQueryChanged = (e) => {
        if (e.key === 'Enter')
            return onValidatedQuery()
        else
            movementMessageStore.setSearchText(e.target.value)
    }

    let onValidatedQuery = () => store.setTextFilter(movementMessageStore.searchText)

    let isMktSaleMovement = (x: MovementListItem) => x.movementType === MovementType.MktSale

    let openAssignableDeals = async (movement: MovementListItem) => {
        setCurrentMovement(movement)

        let assignableDeals = hasDealClaim()
            ? await api.get<Deal[]>(`stock/movement/${movement.id}/deal/assignable`)
            : []

        setAssignableDealsFromMovement(assignableDeals)
        if (assignableDeals.length === 0)
            snackbars.info(t('stock.label.movement.noAssignableDealFound'))
        else
            setOpenAssignDealPopup(true)
    }

    let assignDealFromMovement = async () => {
        await api.post('deal/linkMovement', { dealId: dealIdToAssign, movementId: currentMovement?.id })
        store.loadMovements()
        snackbars.success(t('httpSuccess.dealAssigned'))
    }

    let copyMovementTable = () => copyHtmlElement('movement-table')

    let importGainsAndLosses = async (e) => {
        await api.upload('stock/movement/import/gainsAndLosses', e.target.files[0], 'import')
        store.loadMovements()
        snackbars.success(t('stock.movementTable.import.importGainsAndLossesSucceed'))
    }

    let nominatedMovementsOnForecastOrPlannedIds = selectedMovements
        .filter(x => x.nomination && (x.movementStatus === MovementStatus.Forecast || x.movementStatus === MovementStatus.Planned))
        .map(x => x.id)

    let hasNominatedMovementsOnForecastOrPlanned = nominatedMovementsOnForecastOrPlannedIds.length > 0

    let commentButtonAction = !isMovementTableFullWidth
        ? [{
            isBodyAction: true,
            name: 'MovementComment',
            bodyContentOverride: (x: MovementListItem) => <CommentButton associationKey={`movement-${x.id}`} />
        }] : []

    let actions: ActionDescriptor<MovementListItem>[] = [
        ...commentButtonAction,
        {
            name: 'Deal', action: (x: MovementListItem) => popupNavigator.open('deal', x.associatedDealId),
            icon:
                <Tooltip title={<Typography variant='body1'>{t('vessels.label.purchaseMovement.goToDeal')}</Typography>} placement='top'>
                    <GavelOutlined className='movement-open-deal-icon' />
                </Tooltip>,
            isBodyAction: true,
            disabled: (x: MovementListItem) => !x.associatedDealId
        },
        {
            name: 'LinkDeal',
            action: (x: MovementListItem) => openAssignableDeals(x),
            isBodyAction: true,
            disabled: (x: MovementListItem) => x.associatedDealId != null || !hasFeature('AssignDealFromMovement') || isMktSaleMovement(x),
            icon:
                <Tooltip title={<Typography variant='body1'>{t('stock.movementTable.linkDeal')}</Typography>} placement='top'>
                    <Badge badgeContent={<LinkIcon />}><GavelOutlined /></Badge>
                </Tooltip >
        },
        {
            name: 'History', action: (x: MovementListItem) =>
                historyDialog.open({
                    id: x.id,
                    type: 'movement',
                    name: `${MovementTypeNameFromMovementType(x.movementType)} ${x.subText} ${x.volume} M20, ${x.date
                        ? moment(x.date).format('MM/DD/YYYY') : ''}`,
                    sapFlowId: x!.mainSapFlowId!
                }),
            isBodyAction: true,
            disabled: (x: MovementListItem) => isMktSaleMovement(x),
            icon: <History />
        },
        {
            name: 'Duplicate', action: (x: MovementListItem) => { setCurrentMovement(x); setConfirmDialogOpen(true) },
            icon:
                <Tooltip title={<Typography variant='body1'>{t('stock.movementTable.duplicateMovement')}</Typography>} placement='top'>
                    <FileCopyOutlined />
                </Tooltip>,
            isBodyAction: true,
            disabled: (x: MovementListItem) => isMktSaleMovement(x)
        },
        {
            name: 'Edit', action: (x: MovementListItem) => changeMovementQuantity.open(x),
            icon:
                <Tooltip title={<Typography variant='body1'>{t('stock.movementTable.editQuantity')}</Typography>} placement='top'>
                    <EditOutlined />
                </Tooltip>,
            disabled: (x: MovementListItem) => x.movementStatus === MovementStatus.Actual || isMktSaleMovement(x) || x.nomination,
            isBodyAction: true
        }
    ]

    return (
        <Paper className={classes.container + ' stockBoardPaper' + (className ? ' ' + className : '')}>
            <div className={classes.filtersRow}>
                <div className={classes.tableHeaderRow}>
                    <Typography className={classes.paperTitle} variant='overline' display='block' gutterBottom>
                        {t('stock.movementTable.title')}
                    </Typography>
                    <div className={classes.inoutSwitchContainer}>
                        <Switch
                            isChecked={store.switchFilter == store.outValue}
                            disabled={loader.isActive}
                            changeCallback={() => toggleSwitch()}
                            offText={t('stock.label.movement.in')} onText={t('stock.label.movement.out')} />
                    </div>
                </div>
                {isMovementTableFullWidth && (hasFeature('DailyNomination') || hasFeature('WeeklyNomination')) &&
                    <CustomButton
                        label={t('stock.movementTable.editNominationMvt')}
                        img={<CreateOutlined className={classes.iconStyle} />}
                        disabled={!hasNominatedMovementsOnForecastOrPlanned}
                        className={classes.secondary}
                        onClick={() => batchNominateVolume.open(nominatedMovementsOnForecastOrPlannedIds)} />}
                <div className={classes.searchFieldContainer}>
                    <SearchField
                        expandHorizontally
                        label={t('deals.label.search')}
                        onChange={OnQueryChanged}
                        onClick={onValidatedQuery}
                        text={movementMessageStore.searchText} />
                </div>
                <div className={classes.tableHeaderRow + ' ' + classes.headerButtons}>
                    <div className={classes.mvtsFilters} >
                        <Typography
                            className={isMovementTableFullWidth ? '' : classes.withGrab}
                            variant='subtitle1'
                            onClick={(event: React.MouseEvent) => { if (!isMovementTableFullWidth) setWarningFilterAnchor(event?.currentTarget) }}>
                            {t('stock.movementTable.warningFilter') + getMenuLabelYesNo(store.warningFilter)}</Typography>
                        <Menu
                            icon={isMovementTableFullWidth ? <FilterListOutlined /> : undefined}
                            anchor={isMovementTableFullWidth ? undefined : [warningFilterAnchor, setWarningFilterAnchor]}
                            items={getMenuItemYesNo()}
                            selected={store.warningFilter}
                            onChange={(x: string) => store.setWarningFilter(x)}>
                        </Menu>
                    </div>
                    <div className={classes.mvtsFilters}>
                        <Typography
                            className={isMovementTableFullWidth ? '' : classes.withGrab}
                            onClick={(event: React.MouseEvent) => { if (!isMovementTableFullWidth) setNominationFilterAnchor(event?.currentTarget) }}
                            variant='subtitle1'>
                            {t('stock.movementTable.nominationFilter')}</Typography>
                        {store.nominationFilter === 'true' &&
                            <Typography variant='subtitle1'>{': ' + t('stock.movementTable.menuChoiceYes')}</Typography>}
                        {store.nominationFilter === 'false' &&
                            <Typography variant='subtitle1'>{': ' + t('stock.movementTable.menuChoiceNo')}</Typography>}
                        <Menu
                            icon={isMovementTableFullWidth ? <FilterListOutlined /> : undefined}
                            anchor={isMovementTableFullWidth ? undefined : [nominationFilterAnchor, setNominationFilterAnchor]}
                            items={getMenuItemYesNo()}
                            selected={store.nominationFilter}
                            onChange={(x: string) => store.setNominationFilter(x)}>
                        </Menu>
                    </div>
                    <div className={classes.mvtsFilters}>
                        <Typography
                            className={isMovementTableFullWidth ? '' : classes.withGrab}
                            onClick={(event: React.MouseEvent) => { if (!isMovementTableFullWidth) setDealFilterAnchor(event?.currentTarget) }}
                            variant='subtitle1'>
                            {t('stock.movementTable.dealFilter') + getMenuLabelYesNo(store.dealFilter)}</Typography>
                        <Menu
                            icon={isMovementTableFullWidth ? <FilterListOutlined /> : undefined}
                            anchor={isMovementTableFullWidth ? undefined : [dealFilterAnchor, setDealFilterAnchor]}
                            selected={store.dealFilter}
                            items={getMenuItemYesNo()}
                            onChange={(x: string) => { store.setDealFilter(x) }} >
                        </Menu>
                    </div>
                    {
                        store.stockDateFilter
                            ? <Chip
                                data-tour='movementTableDateFilter'
                                onDelete={store.deleteStockDateFilter}
                                label={moment(store.stockDateFilter).format('MM/DD/YY')} />
                            : null
                    }
                    {
                        isMovementTableFullWidth && <div className={classes.mvtsFilters}>
                            {store.selectedStatus?.length === 1
                                ? <StatusIcon movementStatus={store.selectedStatus[0]} classes={classes} />
                                : <Typography variant='subtitle1'>{t('stock.label.status')}</Typography>}
                            <Menu
                                icon={<FilterListOutlined />}
                                selected={store.selectedStatus?.map(status => MovementStatusNameFromMovementStatus(status))}
                                onChange={onStatusChange}
                                multiSelect={true}
                                items={store.statuses?.map(x =>
                                ({
                                    text: x,
                                    icon: <StatusIcon movementStatus={MovementStatusFromMovementStatusName(x)} classes={classes} />,
                                    textPadding: '40px'
                                })
                                )}
                            >
                            </Menu>
                        </div>}
                    {
                        isMovementTableFullWidth && <div className={classes.mvtsFilters}>
                            {store.selectedMot?.length === 1 && !!store.selectedMot?.first()
                                ? <MovementMotIcon meanOfTransportation={store.selectedMot[0]} />
                                : <Typography variant='subtitle1'>{t('stock.label.mot')}</Typography>}
                            <Menu
                                icon={<FilterListOutlined />}
                                selected={store.selectedMot ?? ''}
                                onChange={onMotChange}
                                multiSelect={true}
                                items={store.mots?.map(x =>
                                ({
                                    text: x,
                                    icon: <MovementMotIcon meanOfTransportation={x} />
                                }))}>
                            </Menu>
                        </div>}
                    {
                        isMovementTableFullWidth && <div className={classes.mvtsFilters}>
                            {store.selectedMovementType !== null && store.selectedMovementType !== undefined
                                ? <MovementTypeIcon movementType={store.selectedMovementType ?? -1} />
                                : <Typography variant='subtitle1'>{t('stock.label.type')}</Typography>}
                            <Menu
                                icon={<FilterListOutlined />}
                                selected={MovementTypeNameFromMovementType(store.selectedMovementType)}
                                onChange={filterOnMovementType}
                                items={store.movementTypes.filter(reduceToActivatedFeatureToggles).map(x =>
                                    ({ text: x, icon: <MovementTypeIcon movementType={MovementTypeFromMovementTypeName(x) ?? MovementType.Unknown} /> }))}
                            >
                            </Menu>
                        </div>}
                    <CustomButton
                        img={<Add />}
                        className={classes.primaryButton}
                        classes={{ root: classes.buttonRoot, label: classes.buttonLabel }}
                        onClick={openMovementCreationPopup} />
                    <CustomDialog isOpen={confirmDialogOpen} title={t('stock.movementTable.confirmationTitle')}
                        contentText={t('stock.movementTable.confirmDuplicate')}
                        confirmButtonText={t('stock.movementTable.duplicate')}
                        onConfirm={async () => {
                            setConfirmDialogOpen(false)
                            if (currentMovement) await handleDuplicateDebouncer.execute(currentMovement)
                        }}
                        onClose={() => setConfirmDialogOpen(false)}
                        onCancel={() => setConfirmDialogOpen(false)} />
                    <CustomDialog isOpen={changeMovementQuantity.isOpen} title={t('stock.movementTable.editQuantityTitle')}
                        content={
                            <NumberField text={changeMovementQuantity.quantity} precision={0} disableNewStyle
                                inputRef={x => changeMovementQuantity.ref.current = x}
                                onKeyDown={evt => {
                                    if (evt.keyCode == EnterKeyCode) {
                                        changeMovementQuantity.save()
                                        evt.preventDefault()
                                    }
                                }}
                                onChange={val => changeMovementQuantity.change(val)} />
                        }
                        onConfirm={() => changeMovementQuantity.save()}
                        onClose={() => changeMovementQuantity.close()}
                        onCancel={() => changeMovementQuantity.close()} />
                    {!isMovementTableFullWidth &&
                        <Menu
                            icon={<MoreVert />}
                            items={[
                                {
                                    text: t('stock.movementTable.export'),
                                    icon: <GetAppOutlined className={classes.iconStyle} />,
                                    onClick: exportMovements
                                },
                                {
                                    text: t('stock.movementTable.delete'),
                                    icon: <DeleteOutlined className={classes.iconStyle} />,
                                    disabled: selectedMovements?.length === 0,
                                    onClick: () => setIsConfirmDeleteOpen(true)
                                },
                                ...(hasFeature('DailyNomination') || hasFeature('WeeklyNomination') ? [{
                                    text: t('stock.movementTable.editNominationMvt'),
                                    icon: <CreateOutlined className={classes.iconStyle} />,
                                    disabled: !hasNominatedMovementsOnForecastOrPlanned,
                                    onClick: () => batchNominateVolume.open(nominatedMovementsOnForecastOrPlannedIds)
                                }] : []),
                                {
                                    text: t('stock.movementTable.copy'),
                                    icon: <FileCopyOutlined className={classes.iconStyle} />,
                                    onClick: copyMovementTable
                                },
                                ...(hasFeature('MovementGainsAndLosses') && !isMovementTableFullWidth ? [{
                                    text: t('stock.movementTable.import.importGainsAndLosses'),
                                    icon: <PublishOutlined className={classes.iconStyle} />,
                                    isUpload: true,
                                    onUpload: importGainsAndLosses
                                }] : [])
                            ]}
                            classesOverride={{ button: classes.menuButton }} />}
                    {isMovementTableFullWidth &&
                        <CustomButton
                            label={t('stock.movementTable.export')}
                            img={<GetAppOutlined className={classes.iconStyle} />}
                            className={classes.secondary}
                            onClick={exportMovements} />}
                    {hasFeature('MovementGainsAndLosses') && isMovementTableFullWidth &&
                        <CustomButton
                            label={t('stock.movementTable.import.importButton')}
                            img={<PublishOutlined className={classes.iconStyle} />}
                            className={classes.secondary}>
                            <Input style={{ display: 'none' }} type='file' onChange={e => { importGainsAndLosses(e); e.target.value = ""; }} />
                        </CustomButton>}
                    {isMovementTableFullWidth &&
                        <CustomButton
                            img={<DeleteOutlined className={classes.iconStyle} />}
                            disabled={selectedMovements?.length === 0}
                            onClick={() => setIsConfirmDeleteOpen(true)} />}
                    {isMovementTableFullWidth &&
                        <CustomButton
                            label={t('stock.movementTable.copy')}
                            img={<FileCopyOutlined className={classes.iconStyle} />}
                            onClick={copyMovementTable} />}
                </div>
            </div>
            {isMovementTableFullWidth && <div className={classes.sapFiltersRow}>
                <SapFilters classes={classes}
                    inOutSwitch={store.switchFilter}
                    onClick={(sapTypes: SapTypes[]) => store.setSapTypesFilter(sapTypes)} />
            </div>}
            <div className={classes.tableContainer}>
                <DataTable tableId={'movement-table'}
                    items={getMovementItems()}
                    idSelector={(x: MovementListItem) => x.id}
                    onClick={onSelect}
                    columns={getMovementTableColumns()}
                    actionsDisabled={loader.isActive}
                    onSelectionChange={(x: MovementListItem[]) => {
                        setSelectedMovements(x)
                        let notMktSaleMovements = x.filter(x => x.movementType !== MovementType.MktSale)
                        movementMessageStore.setSelectedMovementIds(notMktSaleMovements.map(x => x.id))
                    }}
                    actions={actions}
                    groups={isMovementTableFullWidth ? movementTableGroups : undefined} />
            </div>
            {hasFeature('AssignDealFromMovement') &&
                <>
                    <SelectAssignableDeals
                        setOpenAssignDealPopup={setOpenAssignDealPopup}
                        setDealId={setDealIdToAssign}
                        setIsConfirmDealOpen={setIsConfirmDealOpen}
                        products={stockFiltersStore.filters.productIdsFilter.items.map(x => { return { code: x.code, id: x.id } })}
                        counterpartys={store.counterpartys.map(x => { return { name: x.name, id: x.id } })}
                        openAssignDealPopup={openAssignDealPopup}
                        assignableDealsFromMovement={assignableDealsFromMovement} />
                    <ConfirmDealAssignation
                        setIsConfirmDealOpen={setIsConfirmDealOpen}
                        isConfirmDealOpen={isConfirmDealOpen}
                        setOpenAssignDealPopup={setOpenAssignDealPopup}
                        setDealIdToAssign={setDealIdToAssign}
                        trySave={() => { }}
                        openAssignDealPopup={openAssignDealPopup}
                        assignableDealsFromMovement={assignableDealsFromMovement}
                        assignDealFromMovement={assignDealFromMovement}
                        dealIdToAssign={dealIdToAssign}
                    />
                </>}
            <CustomDialog isOpen={isConfirmDeleteOpen}
                title={t('stock.movementTable.confirmDeleteTitle')}
                contentText={t('stock.movementTable.confirmDeleteText')}
                confirmButtonText={t('deals.table.simpleConfirmDelete.confirm')}
                onConfirm={deleteMovements}
                onCancel={() => setIsConfirmDeleteOpen(false)}
                onClose={() => setIsConfirmDeleteOpen(false)}
            />
            <CustomDialog isOpen={batchNominateVolume.isOpen}
                title={t('stock.movementTable.confirmEditNominationTitle')}
                content={
                    <NumberField text={batchNominateVolume.nominatedVolume} precision={0} disableNewStyle
                        inputRef={x => batchNominateVolume.ref.current = x}
                        onKeyDown={evt => {
                            if (evt.keyCode == EnterKeyCode) {
                                batchNominateVolume.save()
                                evt.preventDefault()
                            }
                        }}
                        onChange={val => batchNominateVolume.change(val)} />
                }
                onConfirm={() => batchNominateVolume.save()}
                onClose={() => batchNominateVolume.close()}
                onCancel={() => batchNominateVolume.close()}
            />
            {!isMovementTableFullWidth && <Menu
                selected={store.selectedStatus?.map(status => MovementStatusNameFromMovementStatus(status))}
                onChange={onStatusChange}
                multiSelect={true}
                anchor={[movementStatusFilterAnchor, setMovementStatusFilterAnchor]}
                items={store.statuses?.map(x =>
                ({
                    text: x,
                    icon: <StatusIcon movementStatus={MovementStatusFromMovementStatusName(x)} classes={classes} />,
                    textPadding: '40px'
                })
                )}
            >
            </Menu>}
            {!isMovementTableFullWidth && <Menu
                anchor={[motFilterAnchor, setMotFilterAnchor]}
                icon={<FilterListOutlined />}
                selected={store.selectedMot ?? ''}
                onChange={onMotChange}
                multiSelect={true}
                items={store.mots?.map(x =>
                ({
                    text: x,
                    icon: <MovementMotIcon meanOfTransportation={x} />
                }))}>
            </Menu>}
            {!isMovementTableFullWidth && <Menu
                icon={<FilterListOutlined />}
                selected={MovementTypeNameFromMovementType(store.selectedMovementType)}
                anchor={[movementTypeFilterAnchor, setMovementTypeFilterAnchor]}
                onChange={(x: string) => filterOnMovementType(x)}
                items={store.movementTypes.filter(reduceToActivatedFeatureToggles).map(x =>
                    ({ text: x, icon: <MovementTypeIcon movementType={MovementTypeFromMovementTypeName(x) ?? MovementType.Unknown} /> }))}>
            </Menu>}
        </Paper >)
}

let styles = _ =>
    createStyles({
        errorField: {
            color: defaultColors.red.light.color,
            display: 'flex'
        },
        iconStyle: { height: '1.5rem', width: '1.5rem', marginTop: '-0.2rem' },
        invisible: { opacity: 0 },
        red: { color: defaultColors.red.light.color },
        grey: { color: defaultColors.grey.main.color },
        black: { color: 'black' },
        green: { color: defaultColors.green.main.color },
        filtersRow: {
            ...defaultStyles.flexRow,
            alignItems: 'stretch',
            justifyContent: 'space-between',
            margin: '0.2em 1em',
            marginRight: '0',
            height: '3.2em'
        },
        sapFiltersRow: {
            ...defaultStyles.flexRow,
            justifyContent: 'end',
            margin: '0.2em 1em',
            height: '3.2em',
        },
        container: {
            height: '100%',
            ...defaultStyles.flexColumn,
            alignItems: 'stretch'
        },
        tableHeaderRow: {
            ...defaultStyles.flexRow,
            marginRight: "2px"
        },
        tableContainer: {
            overflow: 'auto',
            "& td": {
                padding: "3px 8px"
            }
        },
        headerButtons: {
            '& > *': {
                marginRight: '1rem',
                '&:last-child': {
                    marginRight: '0em'
                }
            }
        },
        paperTitle: {
            color: defaultColors.red.main.color,
        },
        primaryButton: {
            ...defaultStyles.primaryButton,
            width: '5px',
            height: '40px',
            borderRadius: '7px'
        },
        secondaryButton: {
            ...defaultStyles.secondaryButton
        },
        sapButtonContainer: {
            display: 'inline-block',
            marginLeft: '0.2em',
            marginRight: '0.2em'
        },
        sapButton: {
            ...defaultStyles.secondaryButton,
            height: '2.5em',
            width: '2.5em',
            minWidth: '2.5em',
            padding: '0px',
        },
        sapButtonSelected: {
            ...defaultStyles.primaryButton,
            height: '2.5em',
            width: '2.5em',
            minWidth: '2.5em',
            padding: '0px'
        },
        textSubTextContainer: {
            ...defaultStyles.flexColumn,
            alignItems: 'flex-start'
        },
        subText: {
            fontSize: '0.85em',
            fontStyle: 'italic',
            color: defaultColors.grey.main.color
        },
        movementTypeChip: {
            color: defaultColors.darkBlue.main.color,
            border: '1px solid',
            borderColor: defaultColors.darkBlue.main.color,
            cursor: 'pointer',
            height: '2em',
            width: '7em',
            margin: '0px 0.3em',
            backgroundColor: 'transparent',
            '&:hover': {
                color: defaultColors.darkBlue.dark.color,
                backgroundColor: 'transparent',
            },
            '&:active': {
                color: defaultColors.darkBlue.light.color,
                backgroundColor: 'transparent',
            },
        },
        movementTypeChipSelected: {
            color: defaultColors.darkBlue.main.color,
            border: '1px solid',
            borderColor: defaultColors.darkBlue.main.color,
            cursor: 'pointer',
            height: '2em',
            width: '7em',
            margin: '0px 0.3em',
            backgroundColor: defaultColors.lightBlue.light.color + '55',
            '&:hover': {
                color: defaultColors.darkBlue.dark.color,
                backgroundColor: defaultColors.lightBlue.light.color + '55',
            },
            '&:focus': {
                color: defaultColors.darkBlue.dark.color,
                backgroundColor: defaultColors.lightBlue.light.color + '55',
            },
            '&:active': {
                color: defaultColors.darkBlue.light.color,
                backgroundColor: defaultColors.lightBlue.light.color + '55',
            },
        },
        mvtsFilters: {
            ...defaultStyles.flexRow,
            fontFamily: "\"Roboto\", \"Helvetica\", \"Arial\", sans-serif"
        },
        flexRow: {
            display: 'flex',
            alignItems: 'center'
        },
        buttonRoot: { minWidth: '40px' },
        buttonLabel: {
            width: '100%',
            display: 'inline-flex',
            flexDirection: 'column'
        },
        exportButton: {
            marginRight: '5px'
        },
        withGrab: {
            '&:hover': {
                cursor: 'grab'
            }
        },
        inoutSwitchContainer: {
            marginLeft: '1em'
        },
        searchFieldContainer: {
            flexGrow: 1,
            display: 'flex',
            alignSelf: 'center',
            margin: '1em'
        },
        overflow: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        }
    })

export let MovementTable = withStyles(styles, muiOptions)(_MovementTable)